import React from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import PropTypes from "prop-types";
import { getLongDate } from "../../services/dateService";
import SFTypography from "../typography";
import { SFStack } from "../grid";
import { isJsonObject } from "../../helpers/objFunctions";

const displayValue = (value) => {
  return moment(value, "YYYY-MM-DD", true).isValid()
    ? getLongDate(value)
    : value;
};

const RuleValue = ({ value, between = false }) => {
  const { t } = useTranslation();
  //case null, not null
  if (!value) {
    return null;
  }
  let isValueJsonObject = isJsonObject(value);
  //values is json array of values
  if (isValueJsonObject) {
    let values = JSON.parse(value);
    //case between, not between
    if (between) {
      return (
        <>
          <SFTypography variant="body2bold" color="grey450">
            {displayValue(values[0])}
          </SFTypography>
          <SFTypography variant="body2bold" color="grey350">
            {t("previewQuery.and")}
          </SFTypography>
          <SFTypography variant="body2bold" color="grey450">
            {displayValue(values[1])}
          </SFTypography>
        </>
      );
    } else {
      //case select with multiple values
      return values.map((v, i) => (
        <React.Fragment key={i}>
          {i !== 0 && (
            <SFTypography variant="body2bold" color="grey350">
              {t("previewQuery.or")}
            </SFTypography>
          )}
          <SFTypography variant="body2bold" color="grey450">
            {displayValue(v)}
          </SFTypography>
        </React.Fragment>
      ));
    }
  } else {
    return (
      <SFTypography variant="body2bold" color="grey450">
        {displayValue(value)}
      </SFTypography>
    );
  }
};

const Rule = ({ rule, index, fieldName, valueFormatter }) => {
  const { t } = useTranslation();
  let operatorI18nKey = null;
  let between = false;
  let value = valueFormatter
    ? valueFormatter({ value: rule.value })
    : rule.value;

  switch (rule.operator) {
  case "=":
    operatorI18nKey = "isEqualTo";
    break;
  case "!=":
    operatorI18nKey = "isNotEqualTo";
    break;
  case "<":
    operatorI18nKey = "isLessThan";
    break;
  case ">":
    operatorI18nKey = "isGreatherThan";
    break;
  case "<=":
    operatorI18nKey = "isLessOrEqualTo";
    break;
  case ">=":
    operatorI18nKey = "isGreatherOrEqualTo";
    break;
  case "null":
  case "notNull":
    operatorI18nKey = rule.operator;
    break;
  case "in":
  case "notIn":
    operatorI18nKey = rule.operator;
    break;
  case "between":
  case "notBetween":
    operatorI18nKey = rule.operator;
    between = true;
    break;
  default:
    operatorI18nKey = rule.operator;
  }

  return (
    <React.Fragment key={index}>
      <SFStack direction="row" spacing={0.5} flexWrap="wrap">
        <SFTypography variant="body2" color="grey350">
          {fieldName ?? rule.field}
        </SFTypography>
        <SFTypography variant="body2bold" color="grey350">
          {t(`previewQuery.operator.${operatorI18nKey}`)}
        </SFTypography>
        <RuleValue value={value} between={between} />
      </SFStack>
    </React.Fragment>
  );
};

Rule.propTypes = {
  rule: PropTypes.shape({
    operator: PropTypes.string.isRequired,
    field: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,
  index: PropTypes.number.isRequired,
  fieldName: PropTypes.string,
  valueFormatter: PropTypes.func,
};

export default Rule;
